import {
    ASX_IHub_AppFeedback_FormConfig,
    useFeedbackForm,
    useCooldown,
    FeedbackFormModal,
} from "@amzn/ads-feedback-kit";
import { PhoenixMetrics } from "@amzn/ads-phoenix-type-script-client";
import { ThemeProvider } from "@amzn/storm-ui";
import { useEffect } from "react";
import { useStore } from "zustand";

import { TELEMETRY_ID, PHOENIX_FEATURE } from "../../constants/telemetry";
import { csatFeedbackStore } from "../../store/csatFeedback";
import { isTestSiteOrDev } from "../../utils/environment.utils";
import { logWithTakt } from "../../utils/telemetry.utils";

import { AppCsatFeedbackFormRules } from "./AppCsatHelper";

export const AppCsatFeedback = (): JSX.Element => {
    const {
        checkCooldownIsExpired,
        restartSubmissionCooldown,
        restartCancellationCooldown,
    } = useCooldown({
        surveyName: ASX_IHub_AppFeedback_FormConfig.surveyContext.surveyName,
        surveyGroup: "asx-ihub-app",
        afterCancellation: 7,
        afterSubmission: 60,
        durationUnit: "day",
        adsWideCooldownOptOut: true,
        sessionCooldownOptOut: true,
    });

    const { openFeedbackForm } = useFeedbackForm({
        formConfig: ASX_IHub_AppFeedback_FormConfig,
    });

    const [
        isEligibleToProvideFeedback,
        numberOfSiteVisits,
        incrementNumberOfSiteVisits,
    ] = useStore(csatFeedbackStore, (state) => [
        state.hasUserPerformedActionWorthFeedback,
        state.numberOfSiteVisits,
        state.incrementNumberOfSiteVisits,
    ]);

    /**
     * Expose function to window so we can clear our our feedback status on the test site
     */
    useEffect(() => {
        if (!isTestSiteOrDev()) return;

        (window as any).clearFeedbackStorage = () => {
            localStorage.removeItem("feedback-session-cooldown");
            localStorage.removeItem(
                `feedback-form-cooldown-${ASX_IHub_AppFeedback_FormConfig.surveyContext.surveyName}`,
            );
            localStorage.removeItem(`feedback-group-cooldown-asx-ihub-app`);
        };

        return () => {
            delete (window as any).clearFeedbackStorage;
        };
    }, []);

    useEffect(() => {
        incrementNumberOfSiteVisits();
        if (
            checkCooldownIsExpired() &&
            isEligibleToProvideFeedback &&
            numberOfSiteVisits >= 3
        ) {
            openFeedbackForm();
        }
    }, [checkCooldownIsExpired, openFeedbackForm, isEligibleToProvideFeedback]);

    const handleClose = () => {
        restartCancellationCooldown();
        logWithTakt(TELEMETRY_ID.CSAT_APP_FEEDBACK_CANCELLED);
        PhoenixMetrics.emit(
            TELEMETRY_ID.CSAT_APP_FEEDBACK_CANCELLED,
            PHOENIX_FEATURE.FEEDBACK,
        );
    };

    const handleSubmit = () => {
        restartSubmissionCooldown();
        logWithTakt(TELEMETRY_ID.CSAT_APP_FEEDBACK_SUBMITTED);
        PhoenixMetrics.emit(
            TELEMETRY_ID.CSAT_APP_FEEDBACK_SUBMITTED,
            PHOENIX_FEATURE.FEEDBACK,
        );
    };

    return (
        <ThemeProvider>
            <FeedbackFormModal
                formConfig={ASX_IHub_AppFeedback_FormConfig}
                locale={"en-US"}
                rules={AppCsatFeedbackFormRules}
                allowMultiple={false}
                onClose={handleClose}
                onSubmit={handleSubmit}
            />
        </ThemeProvider>
    );
};
